// ---------------------------------------------------------------------------
// DOM-010 Phase 5 — Cloudflare Custom Hostnames SSL CQRS Event Catalog
// (vocabulary tier)
// ---------------------------------------------------------------------------
// Story DOM-010: "SSL is automatically provisioned/renewed and visible for
// Cloudflare-managed custom domains."
//
// Phase 1 LANDED — closed status / failure-reason / event-name vocabulary
// in `packages/db/src/schema/cf-custom-hostname-ssl-vocabulary.ts`:
//   - `CF_CUSTOM_HOSTNAME_SSL_STATUSES = ["pending_validation",
//     "pending_issuance","active","expired","failed"]`
//   - `CF_CUSTOM_HOSTNAME_SSL_FAILURE_REASONS = ["validation_timed_out",
//     "dcv_error","internal_error"]`
//   - `CF_CUSTOM_HOSTNAME_SSL_STATE_CHANGED_EVENT =
//     "domain.ssl_state_changed@v1"`
//   - state-machine guard `isLegalCfCustomHostnameSslTransition`
//
// Phase 5 (this file): catalogue the per-event payload contract +
// discriminated union the future unified `domain-verifier` worker (couples
// with DOM-018) MUST emit on every SSL state transition per
// DOM-010-DECISION-PACKET §"CQRS event":
//
//   - `domain.ssl_state_changed@v1` — the poller observed CF transition
//     a `(tenantId, customDomainId)` row from `previousStatus` to
//     `currentStatus`; written once per legal transition.
//
// What this file is NOT:
//   - It does not emit events — that's the Phase 6 `domain-verifier`
//     worker.
//   - It does not change Phase 1 vocabulary (additive).
//   - It does not declare an HTTP route — the verifier is a cron worker.
//   - It does not add a database column — the Phase 2 migration that
//     broadens `ssl_status` + adds `ssl_status_reason` is sequenced
//     separately per the decision packet.
//
// Why a separate file from `cf-custom-hostname-ssl-vocabulary.ts`: the
// Phase 1 file pins the closed status/reason vocabulary + state-machine
// guard. This file ADDS the payload shape, discriminated union, and the
// event-type narrowing guard the materializer/DLQ consumers need. Both
// files together form the source of truth for the DOM-010 catalog.
// ---------------------------------------------------------------------------

import {
  CF_CUSTOM_HOSTNAME_SSL_FAILURE_REASONS,
  CF_CUSTOM_HOSTNAME_SSL_STATE_CHANGED_EVENT,
  CF_CUSTOM_HOSTNAME_SSL_STATUSES,
  type CfCustomHostnameSslFailureReason,
  type CfCustomHostnameSslStatus,
} from "./cf-custom-hostname-ssl-vocabulary";

/**
 * Closed catalog of DOM-010 CQRS event types. Versioned with `@v1` suffix
 * per Governance Rule #7 — future shape changes ship `@v2` alongside,
 * never mutate in place. One business fact per event (Governance Rule #6).
 *
 *   - `ssl_state_changed` — the verifier observed CF transition a
 *     `(tenantId, customDomainId)` row from one SSL state to another.
 *     Emitted once per legal transition; idempotency keyed by
 *     `(tenantId, customDomainId, observedAt)` so at-least-once delivery
 *     can be de-duped by the materializer.
 *
 * Sized as a tuple (not single string) so future DOM-010 lifecycle events
 * (`ssl_renewal_attempted@v1`, `ssl_renewal_failed@v1`) can extend without
 * a schema rename.
 */
export const DOM_010_CUSTOM_HOSTNAME_SSL_EVENT_TYPES = [
  CF_CUSTOM_HOSTNAME_SSL_STATE_CHANGED_EVENT,
] as const;

export type Dom010CustomHostnameSslEventType =
  (typeof DOM_010_CUSTOM_HOSTNAME_SSL_EVENT_TYPES)[number];

/**
 * Mirror of the Phase 1 SSL status vocabulary, re-exported here so
 * downstream consumers (DLQ, audit-runner, materializer) can import the
 * event-catalog and the vocabulary from one place. Drift guard test pins
 * these to `CF_CUSTOM_HOSTNAME_SSL_STATUSES` verbatim.
 */
export const DOM_010_SSL_STATUS_VOCAB = [
  ...CF_CUSTOM_HOSTNAME_SSL_STATUSES,
] as const;

export type Dom010SslStatusVocab =
  (typeof DOM_010_SSL_STATUS_VOCAB)[number];

/**
 * Mirror of the Phase 1 SSL failure-reason vocabulary.
 */
export const DOM_010_SSL_FAILURE_REASON_VOCAB = [
  ...CF_CUSTOM_HOSTNAME_SSL_FAILURE_REASONS,
] as const;

export type Dom010SslFailureReasonVocab =
  (typeof DOM_010_SSL_FAILURE_REASON_VOCAB)[number];

/* -------------------------------------------------------------------------- */
/* Runtime narrowing guards                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Runtime narrowing guard. DLQ consumers and the audit-runner use this to
 * reject unknown event types at the boundary instead of silently fanning
 * them downstream.
 */
export function isDom010CustomHostnameSslEventType(
  value: unknown,
): value is Dom010CustomHostnameSslEventType {
  return (
    typeof value === "string" &&
    (DOM_010_CUSTOM_HOSTNAME_SSL_EVENT_TYPES as readonly string[]).includes(
      value,
    )
  );
}

export function isDom010SslStatusVocab(
  value: unknown,
): value is Dom010SslStatusVocab {
  return (
    typeof value === "string" &&
    (DOM_010_SSL_STATUS_VOCAB as readonly string[]).includes(value)
  );
}

export function isDom010SslFailureReasonVocab(
  value: unknown,
): value is Dom010SslFailureReasonVocab {
  return (
    typeof value === "string" &&
    (DOM_010_SSL_FAILURE_REASON_VOCAB as readonly string[]).includes(value)
  );
}

/* -------------------------------------------------------------------------- */
/* Per-event payload contract                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Payload for `domain.ssl_state_changed@v1`. The verifier writes a
 * `previous -> current` snapshot. `reason` is non-null IFF `current` is
 * `"failed"` (Phase 1 vocabulary guarantees the reason is one of
 * `validation_timed_out` / `dcv_error` / `internal_error`). The
 * `cfCustomHostnameId` is the upstream CF Custom Hostnames resource id
 * the poller wrote into `custom_domains.cf_custom_hostname_id`.
 *
 * Mirrors the DOM-010-DECISION-PACKET §"CQRS event" shape: `prev` /
 * `next` / `reason` (when failed) so auditors can replay the state
 * machine. Tenant + customDomain ids are required so the materializer can
 * fan to the per-tenant projection without a secondary lookup.
 */
export interface Dom010SslStateChangedPayload {
  /** Tenant scope for fan-out + isolation. */
  tenantId: string;
  /** FK to `custom_domains.id`. */
  customDomainId: string;
  /** The hostname being verified (mirrors `custom_domains.hostname`). */
  hostname: string;
  /**
   * CF Custom Hostnames resource id from `custom_domains.cf_custom_hostname_id`
   * (Phase 2 column landing alongside the verifier). NULL during the first
   * transition before CF returns an id.
   */
  cfCustomHostnameId: string | null;
  /** SSL status BEFORE the transition. NULL on the initial state insert. */
  previousStatus: Dom010SslStatusVocab | null;
  /** SSL status AFTER the transition. */
  currentStatus: Dom010SslStatusVocab;
  /**
   * Failure reason from `CF_CUSTOM_HOSTNAME_SSL_FAILURE_REASONS`. Non-null
   * IFF `currentStatus === "failed"`. NULL otherwise.
   */
  reason: Dom010SslFailureReasonVocab | null;
  /** ISO-8601 — when the verifier observed the transition. */
  observedAt: string;
}

/**
 * Discriminated union mapping each event type to its payload. Consumers
 * (analytics materializer, DLQ, audit-runner) can `switch (event.type)`
 * and pull the right payload shape without any-casting.
 */
export type Dom010CustomHostnameSslEvent = {
  type: "domain.ssl_state_changed@v1";
  payload: Dom010SslStateChangedPayload;
};
